import React, {MouseEvent, ReactNode} from 'react'
import Timeline, {
    DateHeader,
    DateHeaderProps,
    IntervalRenderer,
    ReactCalendarGroupRendererProps,
    TimelineGroupBase,
    TimelineHeaders,
    TimelineItemBase,
} from 'react-calendar-timeline'
import moment from 'moment'
import 'react-calendar-timeline/dist/Timeline.scss'
import styled from 'styled-components'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import {Tooltip} from '@mui/material'
import {Optional} from '../../../app/TypeHelpers'
import {isWidthDown, WithWidth, WithWidthProps} from '../../shared/forms/hoc/withMediaQuery'
// import withWidth, {isWidthDown, WithWidthProps} from '@mui/material/Hidden/withWidth'

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
// const withWidth = () => (WrappedComponent) => (props) => <WrappedComponent {...props} width="xs" />;

export type Id = string | number

export interface MaxWidth {
    sp: number,
    pc: number
}

interface SidebarProps {
    pcWidth: number
    spWidth: number
}

export interface ReservationTimelineViewProps {
    stackItems?: boolean
    initialVisibleStartDateTime: moment.Moment
    limitStartDateTime?: Optional<moment.Moment>
    limitEndDateTime?: Optional<moment.Moment>
    pixelsAtHour?: number
    maxWidth: MaxWidth
    areaWidth: number
    dateHeaderPropsDay?: DateHeaderProps<number>
    dateHeaderPropsHour: DateHeaderProps<number>
    sidebarProps: SidebarProps
    itemHeightRatio?: number

    activeDay?: moment.Moment
    items: PPTimelineItem[]
    groups: PPTimelineGroup[]

    groupRenderer?: (groupProps: ReactCalendarGroupRendererProps) => ReactNode

    onTimelineClick?: (groupId: Id, itemId: Id | null, time: moment.Moment) => void
    onTimelineHeaderClick?: (date: moment.Moment) => void

    onVisibleRangeChanged?: (
        visibleTimeStart: number,
        visibleTimeEnd: number,
    ) => any

    children: React.ReactNode
}

export interface PPTimelineItem extends Omit<TimelineItemBase<number>, 'title'> {
    contents: string
    tip?: ReactNode
}

export interface PPTimelineGroup extends TimelineGroupBase {
}

interface State {
    visibleTimeStart: number
    visibleTimeEnd: number | undefined
}

export class ReservationTimelineViewBase extends React.Component<ReservationTimelineViewProps & WithWidthProps, State> {
    mouseDownScreenPointX: number | undefined
    mouseDownScreenPointY: number | undefined
    isAdjustVisiblePosition: boolean

    constructor(props: Readonly<ReservationTimelineViewProps & WithWidthProps>) {
        super(props)
        this.state = {
            visibleTimeStart: props.initialVisibleStartDateTime.valueOf(),
            visibleTimeEnd: undefined,
        }
        this.handleTimeChange = this.handleTimeChange.bind(this)
        this.handleBoundsChange = this.handleBoundsChange.bind(this)
        this.mouseDownScreenPointX = undefined
        this.mouseDownScreenPointY = undefined
        this.isAdjustVisiblePosition = true
    }

    componentDidMount(): void {
        this.updateVisibleTerm()
    }

    componentDidUpdate(
        prevProps: Readonly<ReservationTimelineViewProps & WithWidthProps>, prevState: Readonly<State>, snapshot?: any,
    ): void {
        if (prevProps.areaWidth !== this.props.areaWidth) {
            this.updateVisibleTerm()
        }
        if (prevProps.initialVisibleStartDateTime !== this.props.initialVisibleStartDateTime) {
            this.setState({
                visibleTimeStart: this.props.initialVisibleStartDateTime.valueOf(),
                visibleTimeEnd: undefined,
            }, () => {
                this.updateVisibleTerm()
            })
        }
    }

    calculateHoursDuration(): number {
        const {maxWidth, areaWidth, sidebarProps, width, pixelsAtHour} = this.props
        if (!width || !areaWidth) {
            return 0
        }

        const spMode = isWidthDown(width, true)
        const nameWidth = (spMode) ? sidebarProps.spWidth : sidebarProps.pcWidth
        const viewWidth = areaWidth
        const maxTimelineWidth = (spMode && maxWidth.sp > 0)
            ? Math.min(viewWidth, maxWidth.sp)
            : Math.min(viewWidth, maxWidth.pc)
        const timelineWidth = maxTimelineWidth - nameWidth
        return timelineWidth / (pixelsAtHour || 48)
    }

    updateVisibleTerm(): void {
        const hours = this.calculateHoursDuration()
        this.setState(prevState => {
            return {
                visibleTimeStart: prevState.visibleTimeStart,
                visibleTimeEnd: moment(prevState.visibleTimeStart).add(hours, 'hours').valueOf(),
            }
        })
    }

    handleTimeChange(
        visibleTimeStart: number,
        visibleTimeEnd: number,
    ): void {
        if (this.isAdjustVisiblePosition) {
            const scrollToRight = this.state.visibleTimeStart.valueOf() > visibleTimeStart
            const scrollToLeft = this.state.visibleTimeStart.valueOf() < visibleTimeStart
            if (this.props.limitStartDateTime &&
                this.props.limitStartDateTime.valueOf() > visibleTimeStart && scrollToRight) {
                return
            }
            if (this.props.limitEndDateTime &&
                this.props.limitEndDateTime.valueOf() < visibleTimeEnd && scrollToLeft) {
                return
            }
        }
        this.setState({
            visibleTimeStart: visibleTimeStart,
            visibleTimeEnd: visibleTimeEnd,
        }, () => {
            if (this.props.onVisibleRangeChanged) {
                this.props.onVisibleRangeChanged(visibleTimeStart, visibleTimeEnd)
            }
        })
    }

    handleBoundsChange(): void {
        const {visibleTimeStart, visibleTimeEnd} = this.state
        const hours = this.calculateHoursDuration()
        if (this.props.limitStartDateTime === undefined ||
            this.props.limitEndDateTime === undefined) {
            return
        }
        let optimizeLimitStartDateTime = visibleTimeStart
        let optimizeLimitEndDateTime = visibleTimeEnd

        if (this.props.limitStartDateTime.valueOf() > visibleTimeStart) {
            optimizeLimitEndDateTime = moment(this.props.limitStartDateTime).add(hours, 'hours').valueOf()
            optimizeLimitStartDateTime = this.props.limitStartDateTime.valueOf()
        }
        if (visibleTimeEnd && this.props.limitEndDateTime.valueOf() < visibleTimeEnd) {
            optimizeLimitStartDateTime = moment(this.props.limitEndDateTime).subtract(hours, 'hours').valueOf()
            optimizeLimitEndDateTime = this.props.limitEndDateTime.valueOf()
        }
        this.isAdjustVisiblePosition = false
        this.handleTimeChange(optimizeLimitStartDateTime.valueOf(), optimizeLimitEndDateTime!.valueOf())
        this.isAdjustVisiblePosition = true
    }

    render(): ReactNode {
        if (!this.props.width || this.props.areaWidth === 0) {
            return null
        }

        const {
            initialVisibleStartDateTime, onTimelineClick, areaWidth, width,
            itemHeightRatio,
            dateHeaderPropsDay, dateHeaderPropsHour, sidebarProps, onTimelineHeaderClick,
            activeDay,
            stackItems,
            ...restProps
        } = this.props

        const {visibleTimeStart, visibleTimeEnd} = this.state

        const spMode = isWidthDown(width, true)
        const nameWidth = (spMode) ? sidebarProps.spWidth : sidebarProps.pcWidth
        const hours = this.calculateHoursDuration()
        const targetDay = activeDay ? activeDay : initialVisibleStartDateTime
        const minTime = targetDay.clone().startOf('date').valueOf()
        const maxTime = targetDay.clone().add(1, 'days').startOf('date').valueOf()

        return <div style={{maxWidth: `${areaWidth}px`}}>
            <Timeline
                stackItems={stackItems}
                selected={[]}
                lineHeight={50}
                sidebarWidth={nameWidth}
                defaultTimeStart={moment(visibleTimeStart).valueOf()}
                defaultTimeEnd={moment(visibleTimeEnd).valueOf()}
                visibleTimeStart={visibleTimeStart}
                visibleTimeEnd={visibleTimeEnd}
                minZoom={hours * 60 * 60 * 1000}
                maxZoom={hours * 60 * 60 * 1000}
                itemHeightRatio={itemHeightRatio || 1}
                onTimeChange={this.handleTimeChange}
                onBoundsChange={this.handleBoundsChange}
                itemRenderer={({item, itemContext, getItemProps, getResizeProps}) => {
                    const {left: leftResizeProps, right: rightResizeProps} = getResizeProps()
                    const itemProps = item.itemProps ? getItemProps(item.itemProps) : undefined
                    const isRight = (item.start_time < moment(visibleTimeStart).add(hours / 2, 'hours').valueOf())
                    return (
                        <Tooltip title={item.tip || ''} arrow placement={isRight ? 'right' : 'left'}>
                            <div {...itemProps}>
                                {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ''}
                                <ItemOverlay
                                    onMouseDown={(e: MouseEvent) => {
                                        this.mouseDownScreenPointX = e.screenX
                                        this.mouseDownScreenPointY = e.screenY
                                    }}
                                />
                                <div
                                    className="rct-item-content"
                                    style={{maxHeight: `${itemContext.dimensions.height}`}}
                                >
                                    {item.contents}
                                </div>
                                {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ''}
                            </div>
                        </Tooltip>
                    )
                }}
                canMove={false}
                canResize={false}
                onItemSelect={(itemId: number, e: React.SyntheticEvent, time: number) => {
                    if (!onTimelineClick) {
                        return
                    }
                    const target = this.props.items.find(item => item.id === itemId)
                    if (!target) {
                        return
                    }

                    const mouseEvent = (e as MouseEvent)

                    if (this.mouseDownScreenPointX === undefined ||
                        this.mouseDownScreenPointY === undefined) {
                        return
                    }
                    if (mouseEvent.screenX === this.mouseDownScreenPointX &&
                        mouseEvent.screenY === this.mouseDownScreenPointY) {
                        onTimelineClick(target.group, itemId, moment(time))
                    }
                }}
                onItemClick={(itemId: number, e: React.SyntheticEvent, time: number) => {
                    if (!onTimelineClick) {
                        return
                    }
                    const target = this.props.items.find(item => item.id === itemId)
                    if (!target) {
                        return
                    }
                    onTimelineClick(target.group, itemId, moment(time))
                }}
                onCanvasClick={(groupId: number, time: number, _: React.SyntheticEvent): void => {
                    if (!onTimelineClick) {
                        return
                    }
                    onTimelineClick(groupId, null, moment(time))
                }}
                verticalLineClassNamesForTime={(startTime: number, endTime: number): string[] => {
                    if (startTime < minTime || maxTime < endTime) {
                        return ['timeline-out-of-target']
                    }
                    return []
                }}
                {...restProps}
            >
                <TimelineHeaders className={'sticky'}>
                    {
                        dateHeaderPropsDay &&
                        <DateHeader
                            {...dateHeaderPropsDay}
                            intervalRenderer={
                                (intervalRenderProps?: IntervalRenderer<any>): ReactNode => {
                                    if (!intervalRenderProps) {
                                        return null
                                    }
                                    const {onClick, ...removeClickHandlerToDisableClickResizeProps} =
                                        intervalRenderProps.getIntervalProps()
                                    return <div
                                        {...removeClickHandlerToDisableClickResizeProps}
                                        onClick={() => {
                                            if (!onTimelineHeaderClick) {
                                                return
                                            }
                                            onTimelineHeaderClick(
                                                moment(
                                                    intervalRenderProps.intervalContext.interval.startTime.valueOf()
                                                ),
                                            )
                                        }}
                                        className={'rct-dateHeaderDay'}
                                    >
                                        <HeaderDay>
                                            {intervalRenderProps.intervalContext.intervalText}
                                        </HeaderDay>
                                    </div>
                                }}/>
                    }
                    <DateHeader
                        {...dateHeaderPropsHour}
                        intervalRenderer={
                            (intervalRenderProps?: IntervalRenderer<any>): ReactNode => {
                                if (!intervalRenderProps) {
                                    return null
                                }
                                const {onClick, ...removeClickHandlerToDisableClickResizeProps} =
                                    intervalRenderProps.getIntervalProps()
                                return <div
                                    {...removeClickHandlerToDisableClickResizeProps}
                                    className={'rct-dateHeader'}
                                >
                                    {intervalRenderProps.intervalContext.intervalText}
                                </div>
                            }}
                    />
                </TimelineHeaders>
            </Timeline>
        </div>
    }
}

export default WithWidth(ReservationTimelineViewBase)

export const StickyDiv: StyledDiv = styled.div`
.sticky {
  position: sticky;
  top: 0;
  z-index: 82;
}
`

const HeaderDay = styled.div`
  position: sticky;
  left: 10px;
  top: 0;
  display: inline-block;
  font-size: 14px;
`

const ItemOverlay = styled.div`
  position: absolute;
  z-index: 10;
  height: 100%;
  width: 100%;
`
